import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorKeyBindingCommand } from 'const';
import { undoRedoKeyBindings } from 'hooks/useUndoRedo/utils';
import { customCallback } from 'models/UndoRedo';
import {
  StackToggler,
  getLastFromStack,
  isEqual,
  pushStateToStack,
  removeLastFromStack,
  toggleProps,
} from '../../components/ArtboardAssets/utils/stack';
import * as UndoRedoModels from '../../containers/UndoRedoControl/models';

export type UndoRedoMiddleware<StackData> = (
  callback: customCallback,
  prop?: keyof StackData,
  force?: boolean
) => customCallback;

type Props<StackData> = {
  togglersMap: StackToggler<StackData>;
  editMode: boolean;
  currentStackData: StackData;
  saveAppState?: UndoRedoModels.ActionCreator.SaveAppState;
  cancel?: UndoRedoModels.ActionCreator.Cancel;
};

type ExportType<StackData> = {
  undoStack: StackData[];
  redoStack: StackData[];
  undo: (prevData: StackData) => void;
  redo: (prevData: StackData) => void;
  updateUndoRedoBeforeChange: (prevData: StackData) => void;
  undoStackMiddleware: (callback: customCallback, prop?: keyof StackData, force?: boolean) => customCallback;
};

export const useUndoRedo = <StackData>({
  togglersMap, editMode, currentStackData, saveAppState, cancel,
}: Props<StackData>): ExportType<StackData> => {
  const [undoStack, setUndoStack] = useState<StackData[]>([]);
  const [redoStack, setRedoStack] = useState<StackData[]>([]);
  const prevEditMode = useRef<boolean>(false);

  const updateUndoRedoBeforeChange = (newData: StackData): void => {
    setUndoStack(pushStateToStack(undoStack, newData));
    setRedoStack([]);
  };

  const undo = useCallback((prevData: StackData): void => {
    if (!undoStack.length) {
      return;
    }

    const newData = getLastFromStack(undoStack);

    toggleProps(prevData, newData, togglersMap);
    setUndoStack(removeLastFromStack(undoStack));
    setRedoStack(pushStateToStack(redoStack, prevData));
  }, [redoStack, togglersMap, undoStack]);

  const redo = useCallback((prevData: StackData): void => {
    if (!redoStack.length) {
      return;
    }

    const newData = getLastFromStack(redoStack);

    toggleProps(prevData, newData, togglersMap);
    setUndoStack(pushStateToStack(undoStack, prevData));
    setRedoStack(removeLastFromStack(redoStack));
  }, [redoStack, togglersMap, undoStack]);

  const undoStackMiddleware = (callback: customCallback, prop?: keyof StackData, force = false): customCallback =>
    (...valToApply: unknown[]): unknown => {
      if (!prop || !isEqual(valToApply.length === 1 ? valToApply[0] : valToApply, currentStackData[prop]) || force) {
        updateUndoRedoBeforeChange(currentStackData);

        return callback(...valToApply);
      }

      return null;
    };

  useEffect(() => {
    if (!editMode) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent): void => {
      const command = undoRedoKeyBindings(e);
      if (command === EditorKeyBindingCommand.UNDO) {
        undo(currentStackData);
      }

      if (command === EditorKeyBindingCommand.REDO) {
        redo(currentStackData);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, undo, currentStackData, redo]);

  useEffect(() => {
    if (prevEditMode.current !== editMode) {
      if (editMode) {
        saveAppState?.();
      } else {
        if (!undoStack.length) {
          cancel?.();
        }
        setUndoStack([]);
        setRedoStack([]);
      }
      prevEditMode.current = editMode;
    }
  }, [editMode]);

  return {
    undoStack,
    redoStack,
    undo,
    redo,
    updateUndoRedoBeforeChange,
    undoStackMiddleware,
  };
};
